import { TradeType } from '@kodiak-finance/sdk-core'

export interface SerializableTransactionReceipt {
  to: string
  from: string
  contractAddress: string
  transactionIndex: number
  blockHash: string
  transactionHash: string
  blockNumber: number
  status?: number
}

/**
 * Be careful adding to this enum, always assign a unique value (typescript will not prevent duplicate values).
 * These values is persisted in state and if you change the value it will cause errors
 */
export enum TransactionType {
  APPROVAL = 0,
  FARM_DEPLOYMENT,
  FARM_CONFIG_UPDATE,
  FARM_START,
  SWAP,
  DEPOSIT_LIQUIDITY_STAKING,
  WITHDRAW_LIQUIDITY_STAKING,
  CLAIM,
  VOTE,
  DELEGATE,
  WRAP,
  REDEEM,
  CREATE_V3_POOL,
  ADD_LIQUIDITY_V3_POOL,
  ADD_LIQUIDITY_V2_POOL,
  ADD_LIQUIDITY_ISLAND,
  MIGRATE_LIQUIDITY_V3,
  COLLECT_FEES,
  REMOVE_LIQUIDITY_V3,
  REMOVE_LIQUIDITY_V2,
  REMOVE_LIQUIDITY_ISLAND,
  SUBMIT_PROPOSAL,
  QUEUE,
  EXECUTE,
  STAKE_ERC20,
  MINT,
  UNSTAKE_ERC20,
  STAKE_LIQUIDITY_ISLAND,
  CLAIM_FARM_REWARDS,
  REWARD_VAULT_REWARD_CLAIM,
  DEPRECATED_REWARD_VAULT_SLOT_30,
  DEPOSIT_INTO_BAULT,
  REDEEM_FROM_BAULT,
  DEPRECATED_REWARD_VAULT_SLOT_33,
  ADD_LIQUIDITY_AND_STAKE_BAULT,
  UNSTAKE_LIQUIDITY_ISLAND,
  STAKE_CONVERT,
  STAKE_REDEEM,
  DIVIDENDS_ALLOCATE,
  DIVIDENDS_DEALLOCATE,
  DIVIDENDS_HARVEST,
  STAKE_HARVEST_KDK,
  ADD_LIQUIDITY_V2_DEPOSIT_PAGE,
  ADD_LIQUIDITY_ISLAND_SINGLE_ZAP,
  ADD_LIQUIDITY_AND_STAKE_BAULT_SINGLE_ZAP,
  TOKEN_MIGRATION,
  STAKE_CANCEL_REDEEM,
  ADD_STAKING_INCENTIVES,
  SEND_TOKEN,
  AIRDROP_CLAIM,
  CANCEL_SPOT_ORDER,
}

export interface BaseTransactionInfo {
  type: TransactionType
}

export interface UnstakeLiquidityIslandTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.UNSTAKE_LIQUIDITY_ISLAND
  amount: string
}

export interface DeployFarmTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.FARM_DEPLOYMENT
  stakingToken: string
  rewardTokens: string[]
  rewardManagers: string[]
  rewardRates: string[]
}

export interface FarmConfigUpdateInfo extends BaseTransactionInfo {
  type: TransactionType.FARM_CONFIG_UPDATE
  farmAddress: string
  stakingTokenCap?: string
  rewardsDuration?: string
  lockTimeMin?: string
  lockTimeForMaxMultiplier?: string
  maxMultiplier?: string
  rewardTokens?: Array<{
    tokenAddress: string
    rewardRate: string
    rewardManager: string
  }>
}

export interface FarmStartInfo extends BaseTransactionInfo {
  type: TransactionType.FARM_START
  farmAddress: string
}

export interface ClaimFarmRewardsTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.CLAIM_FARM_REWARDS
  rewards: {
    token: string
    amount: string
  }[]
}
export interface RewardVaultRewardClaimTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.REWARD_VAULT_REWARD_CLAIM
  rewardVaultAddress: string
  recipient: string
  amount: number
  rewardTokenSymbol?: string
}

export interface MintLiquidWrapperTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.MINT
  tokenSymbol: string
  amount: string
}

export interface DepositIntoBaultTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.DEPOSIT_INTO_BAULT
  poolId: string
  amount: string
  baultAddress: string
}

export interface AddLiquidityAndStakeBaultTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT
  tokenASymbol?: string
  tokenBSymbol?: string
  tokenAAmount: string
  tokenBAmount: string
  amount: string
  baultAddress: string
}

export interface AddLiquidityIslandSingleZapTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_ISLAND_SINGLE_ZAP
  islandId: string
  inputToken: string // token address or 'native'
  inputTokenSymbol: string
  inputAmount: string
  outputAmount: string // Island tokens received
  tokenASymbol?: string // pool token A
  tokenBSymbol?: string // pool token B
  provider: string // e.g., 'enso'
  isNative: boolean
}

export interface AddLiquidityAndStakeBaultSingleZapTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_AND_STAKE_BAULT_SINGLE_ZAP
  baultAddress: string
  inputToken: string // token address or 'native'
  inputTokenSymbol: string
  inputAmount: string
  outputAmount: string // Bault tokens received
  tokenASymbol?: string // pool token A
  tokenBSymbol?: string // pool token B
  provider: string // e.g., 'enso'
  isNative: boolean
}

export interface RedeemFromBaultTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.REDEEM_FROM_BAULT
  amount: string
  baultAddress: string
}

export interface StakeLiquidityIslandTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.STAKE_LIQUIDITY_ISLAND
  lockTime: string
  stakeAmount: string
  islandId: string
}

export interface StakeERC20TransactionInfo extends BaseTransactionInfo {
  type: TransactionType.STAKE_ERC20
  lockTime: string
  stakeAmount: string
  farmId: string
}

export interface UnstakeERC20TransactionInfo extends BaseTransactionInfo {
  type: TransactionType.UNSTAKE_ERC20
  lockTime: string
  stakeAmount: string
  farmId: string
}

// export interface VoteTransactionInfo extends BaseTransactionInfo {
//   type: TransactionType.VOTE
//   governorAddress: string
//   proposalId: number
//   decision: VoteOption
//   reason: string
// }

export interface QueueTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.QUEUE
  governorAddress: string
  proposalId: number
}

export interface ExecuteTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.EXECUTE
  governorAddress: string
  proposalId: number
}

export interface DelegateTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.DELEGATE
  delegatee: string
}

export interface ApproveTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.APPROVAL
  tokenAddress: string
  spender: string
}

interface BaseSwapTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.SWAP
  tradeType: TradeType
  inputCurrencyId: string
  outputCurrencyId: string
  recipient?: string
  isCustomRecipient?: boolean
}

export interface ExactInputSwapTransactionInfo extends BaseSwapTransactionInfo {
  tradeType: TradeType.EXACT_INPUT
  inputCurrencyAmountRaw: string
  expectedOutputCurrencyAmountRaw: string
  minimumOutputCurrencyAmountRaw: string
}
export interface ExactOutputSwapTransactionInfo extends BaseSwapTransactionInfo {
  tradeType: TradeType.EXACT_OUTPUT
  outputCurrencyAmountRaw: string
  expectedInputCurrencyAmountRaw: string
  maximumInputCurrencyAmountRaw: string
}

export interface DepositLiquidityStakingTransactionInfo {
  type: TransactionType.DEPOSIT_LIQUIDITY_STAKING
  token0Address: string
  token1Address: string
}

export interface WithdrawLiquidityStakingTransactionInfo {
  type: TransactionType.WITHDRAW_LIQUIDITY_STAKING
  token0Address: string
  token1Address: string
}

export interface WrapTransactionInfo {
  type: TransactionType.WRAP
  unwrapped: boolean
  currencyAmountRaw: string
  chainId?: number
}

export interface RedeemTransactionInfo {
  type: TransactionType.REDEEM
  currencyAmountRaw: string
  chainId?: number
}

export interface ClaimTransactionInfo {
  type: TransactionType.CLAIM
  recipient: string
  uniAmountRaw?: string
}

export interface CreateV3PoolTransactionInfo {
  type: TransactionType.CREATE_V3_POOL
  baseCurrencyId: string
  quoteCurrencyId: string
}

export interface AddLiquidityV3PoolTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_V3_POOL
  createPool: boolean
  baseCurrencyId: string
  quoteCurrencyId: string
  feeAmount: number
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

export interface AddLiquidityIslandTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_ISLAND
  islandId: string
  tokenASymbol?: string
  tokenBSymbol?: string
  tokenAAmount: string
  tokenBAmount: string
  feeAmount: string
  expectedAmountBaseRaw: string
}

export interface AddLiquidityV2DepositPageTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_V2_DEPOSIT_PAGE
  poolId: string
  tokenASymbol?: string
  tokenBSymbol?: string
  tokenAAmount: string
  tokenBAmount: string
}

export interface AddLiquidityV2PoolTransactionInfo {
  type: TransactionType.ADD_LIQUIDITY_V2_POOL
  baseCurrencyId: string
  quoteCurrencyId: string
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

export interface MigrateV2LiquidityToV3TransactionInfo {
  type: TransactionType.MIGRATE_LIQUIDITY_V3
  baseCurrencyId: string
  quoteCurrencyId: string
  isFork: boolean
}

export interface CollectFeesTransactionInfo {
  type: TransactionType.COLLECT_FEES
  currencyId0: string
  currencyId1: string
}

export interface RemoveLiquidityV3TransactionInfo {
  type: TransactionType.REMOVE_LIQUIDITY_V3
  baseCurrencyId: string
  quoteCurrencyId: string
  expectedAmountBaseRaw: string
  expectedAmountQuoteRaw: string
}

export interface RemoveLiquidityV2TransactionInfo {
  type: TransactionType.REMOVE_LIQUIDITY_V2
  islandId: string
  tokenA?: string
  tokenB?: string
  sharesBurned: string
  tokenAAmount: string
  tokenBAmount: string
}

export interface RemoveLiquidityIslandTransactionInfo {
  type: TransactionType.REMOVE_LIQUIDITY_ISLAND
  islandId: string
  tokenA?: string
  tokenB?: string
  sharesBurned: string
  tokenAAmount: string
  tokenBAmount: string
}

export interface SubmitProposalTransactionInfo {
  type: TransactionType.SUBMIT_PROPOSAL
}

export interface StakeConvertTransactionInfo {
  type: TransactionType.STAKE_CONVERT
  amount: string
  isStake?: boolean
}

export interface StakeRedeemTransactionInfo {
  type: TransactionType.STAKE_REDEEM
  amount: string
}

export interface StakeHarvestKdkTransactionInfo {
  type: TransactionType.STAKE_HARVEST_KDK
  amount: string
}

export interface StakeCancelRedeemTransactionInfo {
  type: TransactionType.STAKE_CANCEL_REDEEM
  amount: string
}

export interface AddStakingIncentivesTransactionInfo {
  type: TransactionType.ADD_STAKING_INCENTIVES
  amount: string
}

export interface DividendsAllocateTransactionInfo {
  type: TransactionType.DIVIDENDS_ALLOCATE
  amount: string
}

export interface DividendsDeallocateTransactionInfo {
  type: TransactionType.DIVIDENDS_DEALLOCATE
  amount: string
}

export interface DividendsHarvestTransactionInfo {
  type: TransactionType.DIVIDENDS_HARVEST
}

export interface TokenMigrationTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.TOKEN_MIGRATION
  fromToken: string
  toToken: string
  amount: string
}

export interface AirdropClaimTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.AIRDROP_CLAIM
  token: string
  amount: string
}

export interface SendTokenTransactionInfo extends BaseTransactionInfo {
  type: TransactionType.SEND_TOKEN
  tokenAddress: string
  tokenSymbol: string
  recipient: string
  amount: string
  isNative: boolean
}

export type TransactionInfo =
  | ApproveTransactionInfo
  | DeployFarmTransactionInfo
  | FarmConfigUpdateInfo
  | FarmStartInfo
  | ExactOutputSwapTransactionInfo
  | ExactInputSwapTransactionInfo
  | ClaimTransactionInfo
  // | VoteTransactionInfo
  | QueueTransactionInfo
  | ExecuteTransactionInfo
  | DelegateTransactionInfo
  | DepositLiquidityStakingTransactionInfo
  | WithdrawLiquidityStakingTransactionInfo
  | WrapTransactionInfo
  | RedeemTransactionInfo
  | CreateV3PoolTransactionInfo
  | AddLiquidityV3PoolTransactionInfo
  | AddLiquidityV2PoolTransactionInfo
  | AddLiquidityIslandTransactionInfo
  | MigrateV2LiquidityToV3TransactionInfo
  | CollectFeesTransactionInfo
  | RemoveLiquidityV3TransactionInfo
  | RemoveLiquidityV2TransactionInfo
  | RemoveLiquidityIslandTransactionInfo
  | SubmitProposalTransactionInfo
  | StakeLiquidityIslandTransactionInfo
  | ClaimFarmRewardsTransactionInfo
  | RewardVaultRewardClaimTransactionInfo
  | MintLiquidWrapperTransactionInfo
  | DepositIntoBaultTransactionInfo
  | AddLiquidityAndStakeBaultTransactionInfo
  | RedeemFromBaultTransactionInfo
  | UnstakeLiquidityIslandTransactionInfo
  | StakeConvertTransactionInfo
  | StakeRedeemTransactionInfo
  | StakeERC20TransactionInfo
  | UnstakeERC20TransactionInfo
  | AddStakingIncentivesTransactionInfo
  | DividendsAllocateTransactionInfo
  | DividendsDeallocateTransactionInfo
  | DividendsHarvestTransactionInfo
  | StakeHarvestKdkTransactionInfo
  | AddLiquidityV2DepositPageTransactionInfo
  | AddLiquidityIslandSingleZapTransactionInfo
  | AddLiquidityAndStakeBaultSingleZapTransactionInfo
  | StakeCancelRedeemTransactionInfo
  | TokenMigrationTransactionInfo
  | AirdropClaimTransactionInfo
  | SendTokenTransactionInfo
  | { type: TransactionType.CANCEL_SPOT_ORDER }

export interface TransactionDetails {
  hash: string
  receipt?: SerializableTransactionReceipt
  lastCheckedBlockNumber?: number
  addedTime: number
  confirmedTime?: number
  from: string
  info: TransactionInfo
}
